/**
 * Полезная площадь полки — данные окна разметки на странице «Калибровка полигонов».
 *
 * BE: services/services/api/calibration_shelf.py. `useful_area_pct` — объективная
 * вместимость полки: доля кропа, где физически может лежать товар (без скошенных углов
 * полигона, решёток и дверей холодильника). Модель отдаёт площадь товара в долях того же
 * кропа, поэтому сырое / полезная площадь — доля от максимально возможного в абсолютных
 * п.п. Порог алерта (`empty_shelf_score`) — нижняя граница нормы на этой шкале.
 *
 * Пока у полки задана поправка модели (`model_correction_max_pct`, временная подгонка
 * под модель), делит она: делитель = поправка → полезная площадь → 0.8.
 */
import { axiosInstance } from './api';

/** Кадр полки за окно: почти пустая, типичная, самая полная. */
export interface CalibrationShelfCandidate {
  tag: 'low' | 'mid' | 'high';
  /** Квантиль, по которому выбран кадр: 0.01 / 0.5 / 0.98. */
  q: number;
  frame_id: number;
  at: string;
  /** Сырое значение модели на этом кадре, 0..1. */
  raw: number;
  item_id: number;
  /** Кроп полки ровно тот, что видит модель (перспектива полигона, пропорции сохранены). */
  crop_url: string;
  has_masks: boolean;
  mask_percentage: number | null;
}

export type UsefulAreaMode = 'include' | 'exclude';
/** Точка в долях кропа: [x, y], 0..1. */
export type FracPoint = [number, number];

export interface UsefulAreaMarkup {
  mode: UsefulAreaMode;
  polygons: FracPoint[][];
  /** Полезная доля кропа, посчитанная сервером. */
  pct: number;
  /** Доля кропа под контурами (в режиме exclude — убранная). */
  drawn_pct: number;
  frame_id: number | null;
  marked_at: string;
  marked_by: string;
}

export interface CalibrationShelfReference {
  shelf: {
    id: number; name: string; rack_id: number; rack_name: string; rack_type: string;
    predict_percentage: boolean;
    zone_id: number; camera_id: number | null;
  };
  thresholds: {
    empty_shelf_score: number;
    model_correction_max_pct: number | null;
    useful_area_pct: number | null;
    /** Что реально делит сырое сейчас. */
    divisor: number;
  };
  useful_area_markup: UsefulAreaMarkup | null;
  /** Контур полки изменён после разметки — кроп другой, разметку надо повторить. */
  useful_area_stale: boolean;
  window: { from: string; to: string; days: number };
  samples: number;
  frames_with_image: number;
  candidates: CalibrationShelfCandidate[];
}

/** masks=0: маски SAM этому окну не нужны, а каждая — платный вызов. */
export const getCalibrationShelfReference = (shelfId: number, days: number) =>
  axiosInstance.get<CalibrationShelfReference>(
    '/core/api/analytics/calibration-shelf-reference/',
    { params: { shelf_id: shelfId, days, masks: 0 } },
  );

export interface UsefulAreaSaveResult {
  ok: boolean;
  value: number | null;
  previous: number | null;
  divisor: number;
  previous_divisor: number;
  drawn_pct: number;
  /** Сколько строк истории пересчитано (только если делитель сменился). */
  rescaled_rows: number;
  backfill_blocked_before: string | null;
  useful_area_markup: UsefulAreaMarkup | null;
}

/** Пустой polygons снимает разметку и useful_area_pct. */
export const saveShelfUsefulArea = (body: {
  shelf_id: number;
  mode: UsefulAreaMode;
  polygons: FracPoint[][];
  frame_id?: number | null;
  backfill_days?: number;
}) => axiosInstance.post<UsefulAreaSaveResult>(
  '/core/api/analytics/calibration-shelf-useful-area/', body,
);
